import React, { useEffect, useRef, useState } from "react";
import {
  Close,
  LocationOn,
} from "@material-ui/icons";
import ctripService from "../../../services/ctrip.service";

const MIN_SEARCH_LENGTH = 2;
const DEBOUNCE_MS = 350;
const DEFAULT_LOCALE = "en-PK";

const mapPoiToDropoffOption = (poi) => ({
  id: poi.id,
  name: poi.poinm || "",
  subtitle: poi.poiaddr || "",
  type: poi.type || "",
  raw: poi,
});

const parseDropoffLocationsResponse = (response) => {
  const payload = response?.data ?? response;
  const pois = Array.isArray(payload?.pois) ? payload.pois : [];

  return pois.map(mapPoiToDropoffOption).filter((item) => item.name);
};

const getSelectedLocation = (location) => {
  if (!location?.name && !location?.address) {
    return null;
  }

  if (location.address?.poinm) {
    return {
      id: location.address.id,
      name: location.address.poinm,
      subtitle: location.address.poiaddr || "",
      raw: location.address,
    };
  }

  if (location.address?.name) {
    return location.address;
  }

  if (typeof location.name === "string" && location.name) {
    return {
      id: location.name,
      name: location.name,
      subtitle: "",
    };
  }

  return null;
};

const DropoffLocationField = ({
  cityId,
  cityCode,
  pickup,
  currency = "EUR",
  location,
  onChange,
  placeholder = "Search drop-off location",
  error,
  disabled = false,
}) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchError, setSearchError] = useState("");
  const containerRef = useRef(null);
  const sessionRef = useRef({ clientId: null, traceId: null });
  const selected = getSelectedLocation(location);

  useEffect(() => {
    setQuery("");
    setResults([]);
    setIsOpen(false);
    setSearchError("");
    sessionRef.current = { clientId: null, traceId: null };
  }, [cityId]);

  useEffect(() => {
    if (!query.trim() || query.trim().length < MIN_SEARCH_LENGTH) {
      setResults([]);
      setLoading(false);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      setSearchError("");
      try {
        const response = await ctripService.fetchLocationTokens({
          cityId: Number(cityId),
          searchKey: query.trim(),
          currency,
          locale: DEFAULT_LOCALE,
          clientId: sessionRef.current.clientId,
          traceId: sessionRef.current.traceId,
        });

        const payload = response?.data ?? response;
        sessionRef.current = {
          clientId: payload?.clientId ?? null,
          traceId: payload?.traceId ?? null,
        };
        setResults(parseDropoffLocationsResponse(response));
        setIsOpen(true);
      } catch (err) {
        setResults([]);
        setSearchError(err.message || "Failed to search locations.");
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query, cityId, cityCode, pickup, currency]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (item) => {
    onChange({
      name: item.name,
      address: item.raw,
      session: sessionRef.current,
    });
    setQuery("");
    setResults([]);
    setIsOpen(false);
    setSearchError("");
  };

  const handleClear = () => {
    onChange({ name: "" });
    setQuery("");
    setResults([]);
    setIsOpen(false);
    setSearchError("");
  };

  const showResults = isOpen && !loading && results.length > 0;
  const showEmpty =
    isOpen &&
    !loading &&
    query.trim().length >= MIN_SEARCH_LENGTH &&
    results.length === 0 &&
    !searchError;

  return (
    <div
      className="rate-analysis-dropoff-field"
      ref={containerRef}
    >
      {selected && (
        <div className="rate-analysis-dropoff-selected">
          <div className="rate-analysis-dropoff-selected-icon">
            <LocationOn fontSize="small" />
          </div>
          <div className="rate-analysis-dropoff-selected-content">
            <p className="rate-analysis-dropoff-selected-name">{selected.name}</p>
            {selected.subtitle && (
              <p className="rate-analysis-dropoff-selected-subtitle">
                {selected.subtitle}
              </p>
            )}
          </div>
          <button
            type="button"
            className="rate-analysis-dropoff-clear-btn"
            onClick={handleClear}
            disabled={disabled}
            aria-label="Clear selected location"
          >
            <Close fontSize="small" />
          </button>
        </div>
      )}

      <div
        className={`rate-analysis-dropoff-search ${
          disabled ? "rate-analysis-dropoff-search-disabled" : ""
        }`}
      >
        {/* <Search className="rate-analysis-dropoff-search-icon" fontSize="small" /> */}
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => {
            if (results.length > 0) {
              setIsOpen(true);
            }
          }}
          placeholder={
            disabled ? "Select a city first" : placeholder
          }
          disabled={disabled}
          className="rate-analysis-dropoff-search-input"
        />
        {loading && (
          <span className="rate-analysis-dropoff-search-spinner" aria-hidden />
        )}
      </div>

      {searchError && (
        <p className="text-danger font-12 mt-4">{searchError}</p>
      )}

      {showResults && (
        <ul className="rate-analysis-dropoff-results" role="listbox">
          {results.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                className="rate-analysis-dropoff-result-item"
                onClick={() => handleSelect(item)}
                role="option"
              >
                {/* <LocationOn
                  className="rate-analysis-dropoff-result-icon"
                  fontSize="small"
                /> */}
                <span className="rate-analysis-dropoff-result-text">
                  <span className="rate-analysis-dropoff-result-name">
                    {item.name}
                  </span>
                  {item.type && (
                    <span className="rate-analysis-dropoff-result-type">
                      {item.type}
                    </span>
                  )}
                  {item.subtitle && (
                    <span className="rate-analysis-dropoff-result-subtitle">
                      {item.subtitle}
                    </span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {showEmpty && (
        <div className="rate-analysis-dropoff-empty">
          No locations found for &ldquo;{query.trim()}&rdquo;
        </div>
      )}

      {error && <p className="text-danger font-12 mt-4">{error}</p>}
    </div>
  );
};

export default DropoffLocationField;
